import React from 'react';

import { IUser } from '@monetize/types/app';
import { Navigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import { useACL } from '../services/acl/acl';
import { ACLActionItemType } from '../services/acl/actions';
import { useAuth } from '../services/auth0';
import Sentry from '../services/sentry';
import type { JSX } from 'react';

interface IPrivateRouteProps {
  component: JSX.Element;
  path?: string;
  roles?: string[];
  acls?: ACLActionItemType[];
}

function logUnauthorizedAccess(
  reason: string,
  user: IUser | undefined,
  context: { requiredRoles?: string[]; requiredAcls?: ACLActionItemType[] },
) {
  Sentry.logger.error(`Unauthorized page shown: ${reason}`, {
    userId: user?.id,
    userRoles: user?.roles?.map(({ name }) => name),
    ...context,
  });
}

export const PrivateRoute: React.FC<IPrivateRouteProps> = ({
  component: RouteComponent,
  roles,
  acls,
}: IPrivateRouteProps) => {
  const { currentTenantUserHasRole, currentTenantUser } = useAuth();
  const { canDo } = useACL();

  if (roles && !currentTenantUserHasRole(roles)) {
    logUnauthorizedAccess('role check failed', currentTenantUser, {
      requiredRoles: roles,
    });
    return <Navigate to={ROUTES.UNAUTHORIZED_PAGE} replace />;
  }
  if (acls && !canDo(acls)) {
    logUnauthorizedAccess('ACL check failed', currentTenantUser, {
      requiredAcls: acls,
    });
    return <Navigate to={ROUTES.UNAUTHORIZED_PAGE} replace />;
  }

  return RouteComponent;
};
